import React, {FC} from 'react';
import {css} from 'styled-components';

import {directHoverSelector} from '../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {styled, UserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

export interface NavigationLineLabelProps {
  /** Should the label be bold. */
  isUnread?: boolean;
  /** Should the label be all uppercase. */
  isTitle?: boolean;
  /** Is the navigation line selected. */
  isSelected?: boolean;
  /** Override the default color. */
  color?: string;
  /** Override the default hover color. */
  hoverColor?: string;
  /** Whether to allow overflow. */
  shouldAllowOverflow?: boolean;
  /** Whether in P1 edit mode. */
  isEditingWorkspace?: boolean;
}

/*
 * Style.
 */

interface LabelStyleProps {
  $isUnread?: boolean;
  $isTitle?: boolean;
  $isSelected?: boolean;
  $color?: string;
  $hoverColor?: string;
  $shouldAllowOverflow?: boolean;
  $isEditingWorkspace?: boolean;
}

export const navigationLineLabelTextStyle = css`
  line-height: normal;
  font-size: ${fontSizes.medium};
  /* Make it visually more vertically centered. */
  margin-top: -1px;
`;

const StyledLabelDiv = styled.div<LabelStyleProps>`
  grid-area: label;
  align-self: center;
  overflow: ${(p) => (p.$shouldAllowOverflow ? 'visible' : 'hidden')};

  /* Variants. */
  color: ${(p) => getColor(p, p.theme)};
  font-weight: ${(p) => getWeight(p)};
  position: relative;

  ${navigationLineLabelTextStyle};
  ${(p) => maybeAddHoverStyle(p)};
`;

function getColor(props: LabelStyleProps, theme: UserTheme) {
  if (props.$color) {
    return props.$color;
  }

  if (props.$isSelected) {
    return theme.itemSelected;
  }

  if (props.$isUnread) {
    return theme.itemBold;
  }

  return theme.item;
}

function maybeAddHoverStyle(props: LabelStyleProps) {
  if (!props.$hoverColor) {
    return '';
  }

  return css`
    ${directHoverSelector} {
      color: ${props.$hoverColor};
    }
  `;
}

function getWeight(props: LabelStyleProps) {
  if (props.$isEditingWorkspace) {
    return undefined;
  }

  if (props.$isUnread) {
    return 700;
  }

  if (props.$isTitle) {
    return 500;
  }

  return undefined;
}

/*
 * Component.
 */

/** Label for a P1 navigation line. */
export const NavigationLineLabel: FC<NavigationLineLabelProps> = (props) => (
  <StyledLabelDiv
    $isUnread={props.isUnread}
    $isTitle={props.isTitle}
    $isSelected={props.isSelected}
    $color={props.color}
    $hoverColor={props.hoverColor}
    $shouldAllowOverflow={props.shouldAllowOverflow}
    $isEditingWorkspace={props.isEditingWorkspace}
  >
    <TooltipOverflowCondition>{props.children}</TooltipOverflowCondition>
  </StyledLabelDiv>
);
